import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Menu, X, Search, Phone, ChevronDown, ChevronRight, Facebook } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { STORE } from "@/lib/store";
import { api } from "@/lib/api";

const ifdc = (st) => `/boutique?brand=IFDC&subtype=${encodeURIComponent(st)}`;
const APP_CATS = [
  ["refrigerators", "cat.refrigerators"], ["ranges", "cat.ranges"], ["washers", "cat.washers"],
  ["dishwashers", "cat.dishwashers"], ["range-hoods", "cat.range-hoods"], ["freezers", "cat.freezers"],
];

export const Navbar = () => {
  const { count } = useCart();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [subs, setSubs] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    APP_CATS.forEach(([key]) => {
      api.get("/subtypes", { params: { category: key } })
        .then((r) => setSubs((p) => ({ ...p, [key]: (r.data || []).map((x) => x.name) })))
        .catch(() => {});
    });
  }, []);

  const applianceChildren = APP_CATS.map(([key, lk]) => ({
    label: t(lk),
    to: `/boutique?category=${key}`,
    sub: (subs[key] || []).map((s) => ({ label: s, to: `/boutique?category=${key}&subtype=${encodeURIComponent(s)}` })),
  }));

  const groups = [
    { label: t("nav.home"), to: "/" },
    { label: t("nav.appliances"), children: applianceChildren },
    { label: t("nav.furniture"), mega: [
      { header: "Chambre", items: [
        { label: "Tous les lits", to: ifdc("Beds") },
        { label: "Lits plateforme", to: ifdc("Platform Beds") },
        { label: "Ensembles de chambre", to: ifdc("Bedroom Sets") },
        { label: "Lits superposés", to: ifdc("Bunk Beds") },
        { label: "Lits gigognes", to: ifdc("Trundle Beds") },
        { label: "Têtes de lit", to: ifdc("Headboards") },
        { label: "Lits électriques", to: ifdc("Electric Beds") },
        { label: "Commodes & Miroirs", to: ifdc("Dresser & Mirror") },
        { label: "Chiffonniers", to: ifdc("Chests") },
        { label: "Tables de chevet", to: ifdc("Night Stands") },
      ]},
      { header: "Salle à manger", items: [
        { label: "Ensembles à dîner", to: ifdc("Dining Sets") },
        { label: "Chaises", to: ifdc("Dining Chairs") },
        { label: "Bar & Pub", to: ifdc("Bar & Pub") },
      ]},
      { header: "Salon", items: [
        { label: "Sofas", to: ifdc("Sofas") },
        { label: "Sectionnels", to: ifdc("Sectionals") },
        { label: "Fauteuils inclinables", to: ifdc("Recliners") },
        { label: "Meubles de salon", to: ifdc("Living Room") },
      ]},
      { header: "Tables & Accessoires", items: [
        { label: "Tables de salon", to: ifdc("Coffee Tables") },
        { label: "Tables d'appoint", to: ifdc("End & Side Tables") },
        { label: "Tables console", to: ifdc("Console Tables") },
        { divider: true },
        { label: "Voir tous les meubles", to: "/boutique?brand=IFDC" },
      ]},
    ]},
    { label: t("nav.mattresses"), children: [
      { label: "Sleep In Mattress", to: "/boutique?brand=Sleep+In+Mattress" },
      { label: "Matelas IFDC", to: ifdc("Mattresses") },
    ]},
    { label: t("nav.service"), to: "/service" },
    { label: t("nav.used"), to: "/usages" },
    { label: t("nav.articles"), to: "/blog" },
    { label: t("nav.contact"), to: "/contact" },
  ];

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/boutique?search=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/90 backdrop-blur-xl">
      <div className="w-full px-4 xl:px-6">
        <div className="flex items-center justify-between h-[68px] gap-2 lg:gap-3">
          <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
            <img src="/logo.png" alt="DCS Électro" className="h-9 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-2.5 xl:gap-4 text-[13px] xl:text-sm font-semibold">
            {groups.map((g) =>
              g.children || g.mega ? (
                <div key={g.label} className="relative group" data-testid={`nav-group-${g.label}`}>
                  <button className="flex items-center gap-1 py-2 text-gray-800 hover:text-brand-red transition-colors whitespace-nowrap">
                    {g.label} <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                  </button>
                  {g.children && (
                    <div className="absolute left-0 top-full pt-2 hidden group-hover:block z-50">
                      <div className="bg-white border border-gray-200 shadow-xl min-w-[240px] py-2">
                        {g.children.map((c, i) =>
                          c.divider ? <div key={`d${i}`} className="my-1.5 border-t border-gray-100" /> :
                          c.sub && c.sub.length ? (
                            <div key={c.label} className="relative group/sub">
                              <Link to={c.to} className="flex items-center justify-between gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-red transition-colors" data-testid={`nav-item-${c.label}`}>
                                {c.label} <ChevronRight size={14} />
                              </Link>
                              <div className="absolute left-full top-0 pl-1 hidden group-hover/sub:block">
                                <div className="bg-white border border-gray-200 shadow-xl min-w-[240px] py-2">
                                  {c.sub.map((s) => (
                                    <Link key={s.to} to={s.to} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-red transition-colors" data-testid={`nav-subitem-${s.label}`}>{s.label}</Link>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <Link key={c.to} to={c.to} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-red transition-colors" data-testid={`nav-item-${c.label}`}>{c.label}</Link>
                          )
                        )}
                      </div>
                    </div>
                  )}
                  {g.mega && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 hidden group-hover:block z-50">
                      <div className="bg-white border border-gray-200 shadow-2xl w-[860px] p-6 grid grid-cols-4 gap-6">
                        {g.mega.map((col) => (
                          <div key={col.header}>
                            <p className="text-[11px] uppercase tracking-widest text-brand-red font-bold mb-3">{col.header}</p>
                            <div className="space-y-1.5">
                              {col.items.map((c, i) =>
                                c.divider ? <div key={`d${i}`} className="my-2 border-t border-gray-100" /> : (
                                  <Link key={c.to} to={c.to} className="block text-sm text-gray-600 hover:text-brand-red transition-colors" data-testid={`nav-item-${c.label}`}>{c.label}</Link>
                                )
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink key={g.to} to={g.to} className={({ isActive }) => `py-2 transition-colors hover:text-brand-red whitespace-nowrap ${isActive ? "text-brand-red" : "text-gray-800"}`}>{g.label}</NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={doSearch} className="hidden sm:flex items-center border border-gray-300 h-10 px-3 rounded-sm focus-within:border-brand-red">
              <Search size={16} className="text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("nav.search")} className="bg-transparent outline-none px-2 text-sm w-40 sm:w-48 lg:w-32 xl:w-56" data-testid="search-input" />
            </form>
            <LanguageSwitcher />
            <motion.a href={`tel:${STORE.phoneRaw}`}
              animate={{ scale: [1, 1.12, 1] }}
              transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
              className="hidden lg:flex items-center gap-1.5 bg-brand-red text-white text-xs xl:text-sm font-bold px-3 py-2 rounded-full whitespace-nowrap shadow-md hover:bg-[#b81c14]" data-testid="topbar-phone">
              <Phone size={15} /> {STORE.phone}
            </motion.a>
            <a href={STORE.facebook} target="_blank" rel="noopener noreferrer" className="hidden xl:flex p-2 text-brand-navy hover:text-brand-red" data-testid="topbar-facebook" aria-label="Facebook">
              <Facebook size={18} />
            </a>
            <Link to="/panier" className="relative p-2 hover:text-brand-red" data-testid="nav-cart">
              <ShoppingBag size={22} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-brand-red text-white text-[10px] font-bold h-5 w-5 flex items-center justify-center rounded-full" data-testid="cart-count">{count}</span>
              )}
            </Link>
            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        <div className="sm:hidden pb-3">
          <form onSubmit={doSearch} className="flex items-center border border-gray-300 h-11 px-3 rounded-sm focus-within:border-brand-red" data-testid="mobile-search-form">
            <Search size={18} className="text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("nav.search")} className="bg-transparent outline-none px-2 text-sm w-full" data-testid="mobile-search-input" />
          </form>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-200 bg-white max-h-[70vh] overflow-y-auto">
          <nav className="flex flex-col px-4 py-3">
            {groups.map((g) => {
              const flat = g.children ? g.children : g.mega ? g.mega.flatMap((c) => [{ header: c.header }, ...c.items]) : null;
              return flat ? (
                <div key={g.label} className="py-2 border-b border-gray-100">
                  <p className="text-[11px] uppercase tracking-widest text-brand-red font-bold mb-1">{g.label}</p>
                  {flat.filter((c) => !c.divider).map((c, i) =>
                    c.header ? (
                      <p key={`h${i}`} className="text-[10px] uppercase tracking-wider text-gray-400 mt-2 mb-0.5 pl-3">{c.header}</p>
                    ) : c.sub && c.sub.length ? (
                      <div key={c.label}>
                        <Link to={c.to} onClick={() => setOpen(false)} className="block py-1.5 pl-3 text-gray-800 text-sm font-semibold" data-testid={`mnav-item-${c.label}`}>{c.label}</Link>
                        {c.sub.map((s) => (
                          <Link key={s.to} to={s.to} onClick={() => setOpen(false)} className="block py-1 pl-6 text-gray-600 text-[13px]" data-testid={`mnav-subitem-${s.label}`}>{s.label}</Link>
                        ))}
                      </div>
                    ) : (
                      <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="block py-1.5 pl-3 text-gray-700 text-sm" data-testid={`mnav-item-${c.label}`}>{c.label}</Link>
                    )
                  )}
                </div>
              ) : (
                <Link key={g.to} to={g.to} onClick={() => setOpen(false)} className="py-3 border-b border-gray-100 text-gray-800 font-semibold">{g.label}</Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
};
